import { useRef, useState } from 'react';
import {
  Check,
  Clock,
  Copy,
  ImagePlus,
  Landmark,
  QrCode,
  Smartphone,
  TriangleAlert,
} from 'lucide-react';
import { PaymentMethod, UPLOAD } from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP, formatFecha } from '@/lib/cn';
import {
  usePagoPedido,
  useSubirComprobante,
  type CuentaDomiciliario,
  type PagoPedido,
} from '@/lib/queries';
import { Boton, Campo, Entrada, Modal } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

const TIPOS: Record<string, { nombre: string; icono: typeof Smartphone }> = {
  NEQUI: { nombre: 'Nequi', icono: Smartphone },
  DAVIPLATA: { nombre: 'Daviplata', icono: Smartphone },
  BANCOLOMBIA: { nombre: 'Bancolombia', icono: Landmark },
  OTRO_BANCO: { nombre: 'Banco', icono: Landmark },
};

/**
 * Pago por transferencia, desde la pantalla del pedido.
 *
 * El estado lo decide el servidor (`pendiente`) y aquí solo se pinta: si se
 * calculara en el navegador, dos pantallas abiertas podrían mostrar cosas
 * distintas sobre el mismo pedido.
 */
export function PagoTransferencia({ orderId, total }: { orderId: string; total: number }) {
  const pago = usePagoPedido(orderId);

  if (!pago.data || pago.data.paymentMethod !== PaymentMethod.TRANSFER) return null;

  const p = pago.data;

  return (
    <section className="card-kick mt-4 p-5">
      <h2 className="text-lg text-kick-dark">Pago por transferencia</h2>

      {p.pendiente === 'NADA' && p.paymentStatus === 'PAID' && (
        <p className="mt-3 flex items-center gap-2 rounded-kick bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
          <Check className="h-4 w-4 shrink-0" />
          Transferencia verificada. No tienes que pagar nada al recibir.
        </p>
      )}

      {p.pendiente === 'ESPERAR_DOMICILIARIO' && (
        <p className="mt-3 flex items-start gap-2 rounded-kick bg-kick-light px-3 py-2.5 text-sm text-kick-gray">
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          En cuanto se asigne un domiciliario verás su cuenta para transferir. La cuenta es suya:
          por eso no aparece antes.
        </p>
      )}

      {p.pendiente === 'EN_REVISION' && (
        <p className="mt-3 flex items-start gap-2 rounded-kick bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          El domiciliario está verificando tu comprobante en su banco.
        </p>
      )}

      {p.pendiente === 'TRANSFERIR' && <Transferir pago={p} total={total} orderId={orderId} />}

      <Historial pago={p} />
    </section>
  );
}

// ── Transferir ──────────────────────────────────────────────

function Transferir({
  pago,
  total,
  orderId,
}: {
  pago: PagoPedido;
  total: number;
  orderId: string;
}) {
  const [cuentaId, setCuentaId] = useState(
    pago.accounts.find((c) => c.isDefault)?.id ?? pago.accounts[0]?.id ?? '',
  );
  const [subiendo, setSubiendo] = useState(false);

  const ultimoRechazado = pago.receipts.find((r) => r.status === 'REJECTED');
  const cuenta = pago.accounts.find((c) => c.id === cuentaId);

  if (pago.accounts.length === 0) {
    return (
      <p className="mt-3 flex items-start gap-2 rounded-kick bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        El domiciliario todavía no ha registrado una cuenta. Podrás pagar en efectivo al recibir.
      </p>
    );
  }

  return (
    <div className="mt-3">
      {ultimoRechazado && (
        <p className="mb-3 flex items-start gap-2 rounded-kick bg-red-50 px-3 py-2.5 text-sm text-red-800">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <span className="font-semibold">Tu comprobante anterior fue rechazado.</span>{' '}
            {ultimoRechazado.rejectReason}
          </span>
        </p>
      )}

      <p className="text-sm text-kick-gray">
        Transfiere <strong className="text-kick-ink">{formatCOP(total)}</strong> a la cuenta del
        domiciliario y sube la foto del comprobante.
      </p>

      {pago.accounts.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {pago.accounts.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCuentaId(c.id)}
              className={cn(
                'rounded-kick border px-3 py-1.5 text-sm font-semibold transition',
                cuentaId === c.id
                  ? 'border-kick-red bg-kick-red/5 text-kick-red'
                  : 'border-kick-gray/25 text-kick-ink hover:border-kick-gray/50',
              )}
            >
              {TIPOS[c.type]?.nombre ?? c.type}
            </button>
          ))}
        </div>
      )}

      {cuenta && <Cuenta cuenta={cuenta} />}

      <Boton
        className="mt-4 w-full"
        icono={<ImagePlus className="h-4 w-4" />}
        onClick={() => setSubiendo(true)}
      >
        Ya transferí, subir comprobante
      </Boton>

      <ModalComprobante
        abierto={subiendo}
        onCerrar={() => setSubiendo(false)}
        orderId={orderId}
        cuentaId={cuentaId}
        total={total}
      />
    </div>
  );
}

function Cuenta({ cuenta }: { cuenta: CuentaDomiciliario }) {
  const [copiado, setCopiado] = useState(false);
  const Icono = TIPOS[cuenta.type]?.icono ?? Landmark;

  const copiar = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(cuenta.accountNumber);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sin permiso de portapapeles queda a la vista para copiarlo a mano.
    }
  };

  return (
    <div className="mt-3 rounded-kick border border-kick-gray/20 p-4">
      <p className="flex items-center gap-2 font-semibold text-kick-ink">
        <Icono className="h-4 w-4 text-kick-red" />
        {TIPOS[cuenta.type]?.nombre ?? cuenta.type}
        {cuenta.bankName && ` · ${cuenta.bankName}`}
      </p>

      <div className="mt-2 flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate rounded bg-kick-light px-2.5 py-1.5 font-mono text-lg text-kick-ink">
          {cuenta.accountNumber}
        </span>
        <button
          type="button"
          onClick={() => void copiar()}
          aria-label="Copiar el número de cuenta"
          className="rounded-kick p-2 text-kick-gray transition hover:bg-kick-light hover:text-kick-ink"
        >
          {copiado ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>

      <p className="mt-1.5 text-sm text-kick-gray">
        A nombre de {cuenta.holderName}
        {cuenta.accountKind && ` · cuenta de ${cuenta.accountKind.toLowerCase()}`}
        {cuenta.holderDocument && ` · doc. ${cuenta.holderDocument}`}
      </p>

      {cuenta.qrUrl && (
        <div className="mt-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-kick-gray">
            <QrCode className="h-3.5 w-3.5" />O escanea el código
          </p>
          <img
            src={cuenta.qrUrl}
            alt="Código QR para transferir"
            className="mx-auto h-48 w-48 rounded-kick border border-kick-gray/20 object-contain"
          />
        </div>
      )}
    </div>
  );
}

// ── Subida del comprobante ──────────────────────────────────

function ModalComprobante({
  abierto,
  onCerrar,
  orderId,
  cuentaId,
  total,
}: {
  abierto: boolean;
  onCerrar: () => void;
  orderId: string;
  cuentaId: string;
  total: number;
}) {
  const avisos = useAvisos();
  const subir = useSubirComprobante(orderId);
  const entrada = useRef<HTMLInputElement>(null);

  const [archivo, setArchivo] = useState<File | null>(null);
  const [vistaPrevia, setVistaPrevia] = useState<string | null>(null);
  const [referencia, setReferencia] = useState('');

  const elegir = (file: File | undefined): void => {
    if (!file) return;
    if (file.size > UPLOAD.maxImageBytes) {
      avisos.error(`La imagen no puede superar ${UPLOAD.maxImageBytes / 1024 / 1024} MB`);
      return;
    }
    setArchivo(file);
    setVistaPrevia(URL.createObjectURL(file));
  };

  const enviar = (): void => {
    if (!archivo) return;
    subir.mutate(
      {
        archivo,
        accountId: cuentaId || undefined,
        reference: referencia.trim() || undefined,
        // Lo que debía transferir: el domiciliario compara contra su banco.
        declaredAmount: total,
      },
      {
        onSuccess: () => {
          limpiar();
          onCerrar();
          avisos.exito('Comprobante enviado. El domiciliario lo va a verificar.');
        },
        onError: (error) => avisos.error(getErrorMessage(error)),
      },
    );
  };

  const limpiar = (): void => {
    if (vistaPrevia) URL.revokeObjectURL(vistaPrevia);
    setArchivo(null);
    setVistaPrevia(null);
    setReferencia('');
  };

  return (
    <Modal
      abierto={abierto}
      onCerrar={() => {
        limpiar();
        onCerrar();
      }}
      titulo="Subir comprobante"
      descripcion={`Sube la captura de la transferencia por ${formatCOP(total)}.`}
      ancho="sm"
    >
      <div className="p-5">
        <input
          ref={entrada}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => elegir(e.target.files?.[0])}
        />

        {vistaPrevia ? (
          <button
            type="button"
            onClick={() => entrada.current?.click()}
            className="block w-full overflow-hidden rounded-kick border border-kick-gray/20"
          >
            <img src={vistaPrevia} alt="Comprobante" className="max-h-72 w-full object-contain" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => entrada.current?.click()}
            className="flex w-full flex-col items-center gap-2 rounded-kick border border-dashed border-kick-gray/40 px-4 py-10 text-kick-gray transition hover:border-kick-red hover:text-kick-ink"
          >
            <ImagePlus className="h-8 w-8" />
            <span className="text-sm font-semibold">Elegir la captura</span>
            <span className="text-xs">Desde la galería o tomando una foto</span>
          </button>
        )}

        <Campo
          etiqueta="Número de la transacción"
          ayuda="Opcional, pero le facilita la verificación"
          className="mt-4"
        >
          <Entrada
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            placeholder="M1234567"
            maxLength={60}
          />
        </Campo>

        <div className="mt-6 flex justify-end gap-2">
          <Boton
            variante="secundario"
            onClick={() => {
              limpiar();
              onCerrar();
            }}
          >
            Cancelar
          </Boton>
          <Boton disabled={!archivo} cargando={subir.isPending} onClick={enviar}>
            Enviar comprobante
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

// ── Historial ───────────────────────────────────────────────

function Historial({ pago }: { pago: PagoPedido }) {
  if (pago.receipts.length === 0) return null;

  return (
    <ul className="mt-4 space-y-2 border-t border-kick-gray/15 pt-3">
      {pago.receipts.map((comprobante) => (
        <li key={comprobante.id} className="flex items-center gap-3 text-sm">
          <a
            href={comprobante.imageUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0"
            aria-label="Ver el comprobante"
          >
            <img
              src={comprobante.imageUrl}
              alt=""
              className="h-10 w-10 rounded-kick border border-kick-gray/20 object-cover"
            />
          </a>

          <span className="min-w-0 flex-1">
            <span className="block text-kick-gray">{formatFecha(comprobante.createdAt)}</span>
            {comprobante.rejectReason && (
              <span className="block text-xs text-red-600">{comprobante.rejectReason}</span>
            )}
          </span>

          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              comprobante.status === 'APPROVED' && 'bg-emerald-100 text-emerald-800',
              comprobante.status === 'PENDING' && 'bg-amber-100 text-amber-800',
              comprobante.status === 'REJECTED' && 'bg-red-100 text-red-800',
            )}
          >
            {comprobante.status === 'APPROVED'
              ? 'Verificado'
              : comprobante.status === 'PENDING'
                ? 'En revisión'
                : 'Rechazado'}
          </span>
        </li>
      ))}
    </ul>
  );
}
